import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { User, UserType } from "@/types/user";
import { UserService } from "@/services/user/userService";
import { UserDetails } from "@/types/userDetails";
import { mapApiUserTypeToUserType } from "@/utils/authUtils";
import {
  clearNativeTokens,
  getNativeTokens,
  isNativeCapacitorApp,
} from "@/integrations/clients/carAuction/nativeAuth";
import {
  beginNativeAuthBootstrap,
  endNativeAuthBootstrap,
  refreshNativeSession,
  type NativeRefreshResult,
} from "@/integrations/clients/carAuction/carAuctionClient";

const NATIVE_BOOT_ATTEMPTS = 2;
const NATIVE_BOOT_RETRY_MS = 800;
const NATIVE_BACKGROUND_RETRY_MS = 10000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function logNativeBoot(message: string, detail?: unknown): void {
  if (!isNativeCapacitorApp() || !import.meta.env.DEV) return;
  if (detail !== undefined) {
    console.info(`[Bilenia auth] ${message}`, detail);
    return;
  }
  console.info(`[Bilenia auth] ${message}`);
}

function formatBootError(err: unknown): Record<string, unknown> {
  const e = err as {
    message?: string;
    status?: number;
    errorCode?: string | null;
    response?: { status?: number };
  };
  return {
    message: e?.message ?? String(err),
    status: e?.status ?? e?.response?.status ?? null,
    errorCode: e?.errorCode ?? null,
  };
}

function getNativeAccessTokenActorRole(): string | null {
  const token = getNativeTokens()?.accessToken;
  if (!token) return null;
  try {
    const base64 = token.split(".")[1]?.replace(/-/g, "+").replace(/_/g, "/");
    if (!base64) return null;
    const payload = JSON.parse(atob(base64)) as { actorRole?: string };
    return typeof payload.actorRole === "string" ? payload.actorRole : null;
  } catch {
    return null;
  }
}

/** Never throws ? tries both profile endpoints (admin tokens cannot use /users/me). */
async function fetchNativeUserDetailsSafe(): Promise<UserDetails | null> {
  const isAdminToken = getNativeAccessTokenActorRole() === "ADMIN";
  const endpoints: Array<{ label: string; type?: UserType }> = isAdminToken
    ? [
        { label: "/admin/profile", type: UserType.Admin },
        { label: "/users/me" },
      ]
    : [
        { label: "/users/me" },
        { label: "/admin/profile", type: UserType.Admin },
      ];

  for (const { label, type } of endpoints) {
    try {
      const details = await UserService.getUserDetails(type);
      logNativeBoot(`profile ${label} ok`);
      return details;
    } catch (err: unknown) {
      logNativeBoot(`profile ${label} failed`, formatBootError(err));
    }
  }

  return null;
}

function isUnauthorizedError(err: unknown): boolean {
  const e = err as { status?: number; response?: { status?: number } };
  const status = e?.status ?? e?.response?.status;
  return status === 401 || status === 403;
}

async function restoreNativeSession(): Promise<UserDetails | null> {
  if (!getNativeTokens()?.refreshToken) {
    logNativeBoot("restore skipped ? no refresh token");
    return null;
  }

  beginNativeAuthBootstrap();

  try {
    let lastRefresh: NativeRefreshResult = "failed";

    for (let attempt = 0; attempt < NATIVE_BOOT_ATTEMPTS; attempt += 1) {
      logNativeBoot(`restore attempt ${attempt + 1}/${NATIVE_BOOT_ATTEMPTS}`);

      try {
        const existingProfile = await fetchNativeUserDetailsSafe();
        if (existingProfile) {
          logNativeBoot("session restored via existing access token");
          return existingProfile;
        }

        lastRefresh = await refreshNativeSession();
        logNativeBoot("refresh result", lastRefresh);

        if (lastRefresh === "unauthorized") {
          clearNativeTokens();
          logNativeBoot("refresh unauthorized ? tokens cleared");
          return null;
        }

        if (lastRefresh === "ok") {
          const userDetails = await fetchNativeUserDetailsSafe();
          if (userDetails) {
            logNativeBoot("session restored after refresh");
            return userDetails;
          }
          logNativeBoot("profile fetch failed after successful refresh");
        }
      } catch (attemptError: unknown) {
        logNativeBoot("restore attempt error", formatBootError(attemptError));
      }

      if (attempt < NATIVE_BOOT_ATTEMPTS - 1) {
        await sleep(NATIVE_BOOT_RETRY_MS * (attempt + 1));
      }
    }

    if (lastRefresh === "unauthorized") {
      clearNativeTokens();
    } else if (getNativeTokens()?.refreshToken) {
      logNativeBoot("restore exhausted ? tokens kept for retry");
    }

    return null;
  } finally {
    endNativeAuthBootstrap();
  }
}

export interface AuthContextType {
  user: User | null;
  companyId: string | null;
  login: (user: User, companyId?: string | null) => void;
  logout: () => void;
  updateUser: (partialUser: Partial<User>) => void;
  isLoading: boolean;
  /** Native: tokens exist but user profile not loaded yet (avoid /auth flash). */
  isNativeSessionPending: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

function mapUserDetails(userDetails: UserDetails): User {
  const usernameParts = (userDetails.username || "").trim().split(/\s+/);
  return {
    id: userDetails._id,
    firstName: userDetails.firstName || usernameParts[0] || userDetails.username || "",
    lastName:
      userDetails.lastName || usernameParts.slice(1).join(" ") || "",
    email: userDetails.email,
    type: mapApiUserTypeToUserType(userDetails.role),
    phone: userDetails.phoneNumber ?? undefined,
    emailVerified: userDetails.emailVerified ?? true,
    accountTermsAccepted: userDetails.accountTermsAccepted ?? false,
    otherTermsAccepted: userDetails.otherTermsAccepted ?? false,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNativeSessionPending, setIsNativeSessionPending] = useState(false);
  // Invalidate in-flight /users/me bootstrap after logout so stale cookies
  // cannot resurrect the previous session.
  const authEpochRef = useRef(0);

  const applyUserDetails = useCallback((userDetails: UserDetails) => {
    setUser(mapUserDetails(userDetails));
    setCompanyId(userDetails.companyId ?? null);
    setIsNativeSessionPending(false);
  }, []);

  useEffect(() => {
    const epoch = authEpochRef.current;
    const fetchCurrentUser = async () => {
      let nativePending = false;

      try {
        setIsLoading(true);
        setIsNativeSessionPending(false);

        const nativeTokens = isNativeCapacitorApp() ? getNativeTokens() : null;
        if (isNativeCapacitorApp() && !nativeTokens) {
          logNativeBoot("cold start ? no stored tokens");
          return;
        }

        let userDetails: UserDetails | null = null;

        if (isNativeCapacitorApp()) {
          logNativeBoot("cold start ? restoring session");
          userDetails = await restoreNativeSession();
          if (!userDetails && getNativeTokens()?.refreshToken) {
            nativePending = true;
            logNativeBoot("session pending ? keeping dashboard skeleton");
          }
        } else {
          try {
            userDetails = await UserService.getUserDetails(UserType.Admin);
          } catch (adminError: unknown) {
            if (!isUnauthorizedError(adminError)) {
              throw adminError;
            }
            try {
              userDetails = await UserService.getUserDetails();
            } catch {
              return;
            }
          }
        }

        if (authEpochRef.current !== epoch) {
          console.info("[AuthContext] ignoring stale bootstrap after logout");
          return;
        }

        if (!userDetails) return;

        applyUserDetails(userDetails);
      } catch (error) {
        if (authEpochRef.current !== epoch) return;
        if (isNativeCapacitorApp() && getNativeTokens()?.refreshToken) {
          nativePending = true;
        }
        logNativeBoot("bootstrap error", formatBootError(error));
      } finally {
        if (authEpochRef.current === epoch) {
          setIsNativeSessionPending(nativePending);
          setIsLoading(false);
        }
      }
    };

    void fetchCurrentUser();
  }, [applyUserDetails]);

  useEffect(() => {
    if (!isNativeCapacitorApp()) return;

    let active = true;
    let removeListener: (() => void) | undefined;

    void import("@capacitor/app").then(({ App }) => {
      if (!active) return;

      void App.addListener("appStateChange", ({ isActive }) => {
        if (!isActive || user) return;
        if (!getNativeTokens()?.refreshToken) return;

        logNativeBoot("app resumed ? retrying session restore");
        void restoreNativeSession().then((userDetails) => {
          if (!active || !userDetails) return;
          applyUserDetails(userDetails);
        });
      }).then((handle) => {
        if (!active) {
          void handle.remove();
          return;
        }
        removeListener = () => {
          void handle.remove();
        };
      });
    });

    return () => {
      active = false;
      removeListener?.();
    };
  }, [user, applyUserDetails]);

  useEffect(() => {
    if (!isNativeCapacitorApp() || !isNativeSessionPending || user) return;

    let active = true;
    const retry = async () => {
      if (!active || !getNativeTokens()?.refreshToken) return;
      logNativeBoot("background retry ? restoring session");
      const userDetails = await restoreNativeSession();
      if (!active || !userDetails) return;
      applyUserDetails(userDetails);
    };

    const timer = window.setInterval(() => {
      void retry();
    }, NATIVE_BACKGROUND_RETRY_MS);

    void retry();

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [isNativeSessionPending, user, applyUserDetails]);

  const login = useCallback((user: User, companyId?: string | null) => {
    setUser(user);
    setCompanyId(companyId ?? null);
    setIsNativeSessionPending(false);
  }, []);

  const logout = useCallback(() => {
    authEpochRef.current += 1;
    setUser(null);
    setCompanyId(null);
    setIsNativeSessionPending(false);
    setIsLoading(false);
  }, []);

  const updateUser = useCallback((partialUser: Partial<User>) => {
    setUser((prev) => {
      return prev ? ({ ...prev, ...partialUser } as User) : prev;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      companyId,
      login,
      logout,
      updateUser,
      isLoading,
      isNativeSessionPending,
    }),
    [
      user,
      companyId,
      login,
      logout,
      updateUser,
      isLoading,
      isNativeSessionPending,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
